// Privacy Policy - English version

export const privacyContentEN = {
  sections: [
    { id: 'general', title: '1. General Provisions' },
    { id: 'data', title: '2. What Data We Process' },
    { id: 'purposes', title: '3. Purposes and Legal Bases' },
    { id: 'cookies', title: '4. Cookies and Analytics' },
    { id: 'ai-services', title: '5. AI and Cloud Services' },
    { id: 'transfer', title: '6. Data Transfer and Storage' },
    { id: 'rights', title: '7. Data Subject Rights' },
    { id: 'security', title: '8. Security' },
    { id: 'contacts', title: '9. Contact Information' }
  ],
  
  general: {
    title: '1. General Provisions',
    content: `This Policy describes how Polyus Plus OÜ (hereinafter "Company") processes personal data in accordance with Regulation (EU) 2016/679 (GDPR) and the Estonian Personal Data Protection Act.

The Company acts as:

• **Data Controller** (when operating the website and marketing)
• **Data Processor** (when providing services to clients under contracts and DPAs)`
  },
  
  data: {
    title: '2. What Data We Process',
    categories: [
      {
        name: 'Personal Data',
        icon: 'Eye',
        items: ['Name, job title', 'Contact details (e-mail, phone)', 'Form and correspondence data']
      },
      {
        name: 'Company Data (B2B)',
        icon: 'Database',
        items: ['Company name', 'Registration number', 'Legal address']
      },
      {
        name: 'Technical Information',
        icon: 'Globe',
        items: ['IP address', 'Cookies and identifiers', 'Analytics data']
      },
      {
        name: 'Important',
        icon: 'AlertCircle',
        items: ['We do NOT collect sensitive personal data (racial origin, religion, health)']
      }
    ]
  },
  
  purposes: {
    title: '3. Purposes and Legal Bases',
    intro: 'Processing is carried out for:',
    bases: [
      {
        title: 'Contract Performance',
        legal: 'Art. 6(1)(b) GDPR',
        description: 'Communication, invoicing, service provision',
        color: 'blue'
      },
      {
        title: 'Marketing',
        legal: 'Art. 6(1)(a) GDPR',
        description: 'With your consent (can be withdrawn at any time)',
        color: 'green'
      },
      {
        title: 'Analytics and Website Improvement',
        legal: 'Art. 6(1)(f) GDPR',
        description: 'Legitimate interest of the company',
        color: 'cyan'
      },
      {
        title: 'Legal Obligations',
        legal: 'Art. 6(1)(c) GDPR',
        description: 'Tax and accounting legislation',
        color: 'yellow'
      }
    ]
  },
  
  cookies: {
    title: '4. Cookies and Analytics',
    intro: 'The website uses cookies and analytics services to improve user experience and understand how visitors use our site.',
    services: [
      { name: 'Google Analytics 4', description: 'Traffic and behavior analysis' },
      { name: 'Meta Pixel (Facebook)', description: 'Advertising and remarketing' },
      { name: 'Hotjar', description: 'Heatmaps and UX analysis' }
    ],
    note: 'You can manage cookie settings in your browser or through our settings.',
    linkText: 'Cookie Policy'
  },
  
  aiServices: {
    title: '5. AI and Cloud Services',
    intro: 'To perform services, Polyus Plus OÜ uses modern AI platforms and cloud services:',
    platforms: [
      'OpenAI (ChatGPT, DALL-E)',
      'Google Cloud AI',
      'Microsoft Azure',
      'Notion, Airtable'
    ],
    transferNote: 'When transferring data outside the EU, protection is applied in the form of **EU Standard Contractual Clauses (SCC)** and other mechanisms approved by the European Commission.'
  },
  
  transfer: {
    title: '6. Data Transfer and Storage',
    intro: 'Data may be transferred only to:',
    recipients: [
      {
        title: 'Employees and contractors of Polyus Plus OÜ',
        description: 'With signed confidentiality agreements'
      },
      {
        title: 'Hosting, cloud, and analytics providers',
        description: 'AWS, Google Cloud, Vercel, etc. (with DPA)'
      },
      {
        title: 'Government authorities',
        description: 'Only upon lawful requests (court, tax authority)'
      }
    ],
    retention: {
      title: 'Retention Period',
      period: 'Up to 5 years',
      note: 'after the end of the contract, unless otherwise required by law (e.g., tax or accounting legislation — 7 years).'
    }
  },
  
  rights: {
    title: '7. Data Subject Rights',
    intro: 'In accordance with GDPR, you have the following rights:',
    list: [
      { title: 'Right of Access', description: 'Obtain a copy of your personal data' },
      { title: 'Right to Rectification', description: 'Correct inaccurate data' },
      { title: 'Right to Erasure', description: '"Right to be forgotten" (under certain conditions)' },
      { title: 'Right to Restriction', description: 'Restrict processing of your data' },
      { title: 'Right to Portability', description: 'Receive data in a structured format' },
      { title: 'Right to Object', description: 'Withdraw consent to processing' }
    ],
    exercise: {
      title: 'How to Exercise Your Rights?',
      instruction: 'Send a request to info@polyusplus.com. We will respond within 30 days.',
      complaint: 'You also have the right to lodge a complaint with the supervisory authority: **Andmekaitse Inspektsioon** (Estonian Data Protection Authority) — aki.ee'
    }
  },
  
  security: {
    title: '8. Security',
    intro: 'The Company applies modern technical and organizational measures to protect your personal data from unauthorized access, leakage, alteration, or destruction.',
    measures: [
      { title: 'Encryption', description: 'SSL/TLS for data transmission', icon: 'Lock' },
      { title: 'Access Control', description: 'Multi-factor authentication', icon: 'Shield' },
      { title: 'Secure Servers', description: 'AWS, Google Cloud with DPA', icon: 'Database' }
    ],
    breach: {
      title: 'Breach Notification',
      text: 'In case of a personal data breach, we will notify the supervisory authority within 72 hours and you personally if the breach poses a high risk to your rights and freedoms.'
    }
  },
  
  contacts: {
    title: '9. Contact Information',
    intro: 'For questions about personal data processing or to exercise your rights, please contact us:',
    company: 'Polyus Plus OÜ',
    regNumber: '14890902',
    address: 'Uuslinna 11, Tallinn 11415, Estonia',
    email: 'info@polyusplus.com'
  },
  
  gdprNotice: 'This Policy complies with the requirements of Regulation (EU) 2016/679 (GDPR) and the Estonian Personal Data Protection Act. Your data is protected at the highest European level.'
};
